/** @license
 * eventsource.js
 * Available under MIT License (MIT)
 * https://github.com/Yaffle/EventSource/
 */

import EventEmitter from 'events'
import ConnectionStatus from './ConnectionStatus'
import EventSourceRequest from './EventSourceRequest'
import { FetchRequest, isFetchSupported } from './FetchRequest'
import { XHRRequest } from './XHRRequest'

class EventSourcePolyfill {
  static FetchRequest = FetchRequest
  static XHRRequest = XHRRequest
  static CONNECTING = ConnectionStatus.CONNECTING
  static OPEN = ConnectionStatus.OPEN
  static CLOSED = ConnectionStatus.CLOSED

  CONNECTING = ConnectionStatus.CONNECTING
  OPEN = ConnectionStatus.OPEN
  CLOSED = ConnectionStatus.CLOSED

  constructor(url, { RequestClass, headers, lastEventId, withCredentials } = {}) {
    this._events = new EventEmitter()
    this.onerror = undefined
    this.onmessage = undefined
    this.onopen = undefined
    this.readyState = ConnectionStatus.WAITING
    this.url = url = String(url)
    this.withCredentials = withCredentials

    if (!RequestClass) {
      RequestClass = isFetchSupported ? FetchRequest : XHRRequest
    }

    this._esRequest = new EventSourceRequest({
      RequestClass,
      dispatchEvent: event => this.dispatchEvent(event),
      headers,
      lastEventId,
      onError: event => this.onerror && this.onerror(event),
      onMessage: event => this.onmessage && this.onmessage(event),
      onOpen: event => this.onopen && this.onopen(event),
      onReadyStateChange: readyState => { this.readyState = readyState },
      url,
      withCredentials,
    })
  }

  addEventListener(name, handler) {
    this._events.on(name, handler)
  }

  removeEventListener(name, handler) {
    this._events.removeListener(name, handler)
  }

  dispatchEvent(event) {
    if (!event || !event.type) return

    this._events.emit(event.type, event)
  }

  async start() {
    if (this.readyState !== ConnectionStatus.WAITING) return
    await this._esRequest.start()
  }

  close() {
    if (this.readyState === ConnectionStatus.CLOSED) return
    this._esRequest.close()
    this._esRequest.onFinish(new Error('Connection closed by caller'))
  }
}

const NativeEventSource = global.EventSource

export default EventSourcePolyfill
export { EventSourcePolyfill, NativeEventSource }
